"use client";

import { Fragment, useState } from "react";
import { signIn } from "next-auth/react";
import { Button } from "@/components/ui/button";
import {
  describeSignInError,
  microsoftErrorUrl,
  type SignInErrorInfo,
  signInErrorDetails,
} from "@/lib/auth/sign-in-error";

/**
 * Microsoft SSO を開始し、ログイン後は元の画面へ戻す。
 *
 * @remarks
 * `signInError` があるときは失敗の案内と、Microsoft が返したエラーの項目（運用者に伝えるもの）を出す。
 */
export function LoginForm({ callbackUrl = "/", expired = false, signInError = null }: Readonly<{
  callbackUrl?: string; expired?: boolean; signInError?: SignInErrorInfo | null;
}>) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(signInError !== null);
  // 押したあとの失敗（signIn が投げた）は項目を持たないので、案内の文だけを出す
  const [details, setDetails] = useState(signInError);
  async function login() {
    setLoading(true);
    setError(false);
    setDetails(null);
    try {
      await signIn("microsoft-entra-id", { redirectTo: callbackUrl });
    } catch {
      setError(true);
      setLoading(false);
    }
  }
  const explanation = details && describeSignInError(details);
  const rows = details ? signInErrorDetails(details) : [];
  return (
    <section className="w-full max-w-md space-y-6 rounded-xl border bg-card p-8 shadow-sm">
      <div className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Nexus</p>
        <h1 className="text-2xl font-semibold">ログイン</h1>
        <p className="text-sm text-muted-foreground">会社のMicrosoftアカウントでログインしてください。</p>
      </div>
      {expired && <p role="status" className="text-sm">セッションの有効期限が切れました。再度ログインすると、元の画面に戻ります。</p>}
      {error && (
        <div role="alert" className="space-y-2 text-sm">
          <p className="text-destructive">ログインできませんでした。再試行するか、運用者にお問い合わせください。</p>
          {explanation && <p>{explanation}</p>}
          {rows.length > 0 && (
            <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-xs">
              <p className="text-muted-foreground">運用者に問い合わせるときは、次の内容を伝えてください。</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                {rows.map(([label, value]) => (
                  <Fragment key={label}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="break-all font-mono">{value}</dd>
                  </Fragment>
                ))}
              </dl>
              {details?.aadsts && (
                <a
                  href={microsoftErrorUrl(details.aadsts)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block pt-1 underline underline-offset-2"
                >
                  Microsoft のエラーの説明（英語）を開く
                </a>
              )}
            </div>
          )}
        </div>
      )}
      <Button className="w-full" onClick={login} disabled={loading}>
        {loading ? "Microsoftへ移動中…" : "Microsoftでログイン"}
      </Button>
    </section>
  );
}
